// use-remove-team-member.ts — Teams suite, action
//
// DELETE /api/teams/:teamId/members/:memberId. Removes a member from a team.

'use client';

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/contexts/api';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import { teamsDetailKey, teamsRootKey } from './team-query-keys';

export function useRemoveTeamMember(): ActionResult<{ teamId: string; memberId: string }, void> {
  const api = useApi();
  const queryClient = useQueryClient();

  return useActionHook<{ teamId: string; memberId: string }, void>(
    useMemo(
      () => ({
        mutationFn: async ({ teamId, memberId }) => {
          await api.teams.byId(teamId).members.byId(memberId).delete();
        },
        onInvalidate: (_data, { teamId }) => {
          queryClient.invalidateQueries({ queryKey: teamsDetailKey(teamId) });
          queryClient.invalidateQueries({ queryKey: teamsRootKey });
        },
      }),
      [api, queryClient],
    ),
  );
}
